import { ChangeDetectorRef, SimpleChanges } from "@angular/core";
import { Subject } from "rxjs";

/**
 * Presentador base abstracto que maneja el ciclo de vida de las vistas
 * 
 * @template T - Tipo de la vista que manejará el presentador
 * 
 * @example
 * ```typescript
 * export class UserPresenter extends IAbstractPresenter<UserView> {
 *   constructor(private userService: UserService) {
 *     super();
 *   }
 * 
 *   dispatchActions(): void {
 *     this.userService.getUsers().subscribe(users => {
 *       this.view.showUsers(users);
 *     });
 *   }
 * }
 * ```
 */

export abstract class IAbstractPresenter<T> {
  public view!: T;
  public cdr!: ChangeDetectorRef;
  public destroy$ = new Subject<void>();

  /**
   * Método que establece la vista y el change detector del presentador
   * @param view - Componente que manejará el presentador
   * @param cdr - Change detector del componente
   * @returns void
   */
  setView(view: T, cdr: ChangeDetectorRef): void {
    this.view = view;
    this.cdr = cdr;

    this.onInit?.();
    this.dispatchActions();
    this.suscribeObservers();
  }  

  /**
   * Método que limpia las suscripciones
   * @returns void
   */
  onDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Método que se ejecuta cuando el presentador es inicializado
   * @returns void
   */
  onInit?(): void;

  /**
   * Método que ejecuta las acciones del presentador
   * @returns void
   */
  abstract dispatchActions(): void;

  /**
   * Método que ejecuta las suscripciones del presentador
   * @returns void
   */
  abstract suscribeObservers(): void;

  /**
   * Método que se ejecuta después de que la vista ha sido inicializada
   * @returns void
   */
  onAfterViewInit?(): void;

  /**
   * Método que se ejecuta después de que la vista ha cambiado
   * @returns void
   */
  onChanges?(changes: SimpleChanges): void;

  /**
   * Método que se ejecuta para validar la instancia
   * @returns boolean
   */
  validateInstance?(): boolean | undefined;
}
